import { useState } from "react";
import axios from "axios";
import { CONTACT_INFO } from "../data/content";
import { useReveal } from "../hooks/useReveal";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin, Clock, ArrowUpRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Residential",
  "Hospitality",
  "Corporate",
  "Healthcare",
  "Retail",
  "Restaurants",
  "Showrooms",
  "Other",
];

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Thank you. Our design team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-40 bg-[#FAFAFA]"
    >
      <div
        ref={ref}
        className="reveal max-w-[90rem] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16"
      >
        {/* Left column - form */}
        <div className="lg:col-span-7">
          <p className="overline mb-6 flex items-center gap-3">
            Contact
          </p>
          <h2
            data-testid="contact-headline"
            className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-stone-900"
          >
            Tell us about
            <em className="italic text-[#4A6076]"> your space</em>.
          </h2>
          <p className="mt-6 max-w-lg text-stone-600 leading-relaxed">
            Share a few details about your project. Our design team will respond
            within one working day with next steps.
          </p>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"
          >
            <div className="sm:col-span-1">
              <label className="overline block mb-2">Name *</label>
              <input
                data-testid="contact-input-name"
                className="field-underline"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your full name"
                required
              />
            </div>
            <div className="sm:col-span-1">
              <label className="overline block mb-2">Email *</label>
              <input
                data-testid="contact-input-email"
                type="email"
                className="field-underline"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="sm:col-span-1">
              <label className="overline block mb-2">Phone</label>
              <input
                data-testid="contact-input-phone"
                className="field-underline"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 · · · · · · · · · ·"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="overline block mb-2">Project Type</label>
              <Select
                value={form.project_type}
                onValueChange={(v) => update("project_type", v)}
              >
                <SelectTrigger
                  data-testid="contact-input-project-type"
                  className="border-0 border-b border-stone-300 rounded-none px-0 py-3 h-auto bg-transparent focus:ring-0 focus:border-[#4A6076] text-stone-800 shadow-none"
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="overline block mb-2">Tell us about your project *</label>
              <textarea
                data-testid="contact-input-message"
                className="field-underline resize-none"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Rooms, moods, timelines, references..."
                required
              />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-4 pt-4">
              <p className="text-[12px] text-stone-500 max-w-sm">
                By submitting, you agree to our privacy policy. We reply within
                one working day.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending…" : "Send Enquiry"}
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>

        {/* Right column - details */}
        <div className="lg:col-span-5">
          <div className="glass rounded-sm p-8 lg:p-10 mb-8">
            <p className="overline mb-6">Studio</p>
            <ul className="space-y-6 text-stone-800">
              <li className="flex items-start gap-4" data-testid="contact-email">
                <Mail size={18} strokeWidth={1.4} className="mt-1 text-[#4A6076]" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-stone-500">Email</p>
                  <a href={`mailto:${CONTACT_INFO.email}`} className="link-underline text-base">
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <Phone size={18} strokeWidth={1.4} className="mt-1 text-[#4A6076]" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-stone-500">Phone</p>
                  <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`} className="link-underline text-base">
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-whatsapp">
                <MessageCircle size={18} strokeWidth={1.4} className="mt-1 text-[#4A6076]" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-stone-500">WhatsApp</p>
                  <a
                    href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="link-underline text-base"
                  >
                    {CONTACT_INFO.whatsapp}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin size={18} strokeWidth={1.4} className="mt-1 text-[#4A6076]" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-stone-500">Locations</p>
                  <p className="text-base leading-relaxed tracking-[0.15em] uppercase">{CONTACT_INFO.address}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Clock size={18} strokeWidth={1.4} className="mt-1 text-[#4A6076]" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-stone-500">Hours</p>
                  <p className="text-base">{CONTACT_INFO.hours}</p>
                </div>
              </li>
            </ul>

            <div className="mt-8 pt-6 border-t border-stone-200 flex flex-wrap gap-4">
              {CONTACT_INFO.socials.map((s) => (
                <a
                  key={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] tracking-[0.25em] uppercase text-stone-600 hover:text-stone-900 link-underline"
                >
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
